import { useState } from 'react';
import { logger } from '../../utils/logger';

const SkillDistributionChart = ({ player, comparison, allPlayers = [] }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Debug logging
  logger.debug('SkillDistributionChart data:', {
    player,
    comparison,
    allPlayersCount: allPlayers.length,
    allPlayers: allPlayers.map(p => ({
      id: p.id,
      name: p.name,
      mu: p.mu,
      sigma: p.sigma,
    })),
  });

  const getPath = (mu, sigma, height, width) => {
    const points = [];
    const start = 0;
    const end = 50;
    const step = (end - start) / 200;
    const pdf = x =>
      (1 / (sigma * Math.sqrt(2 * Math.PI))) *
      Math.exp(-0.5 * Math.pow((x - mu) / sigma, 2));

    // Calculate dynamic max value based on this distribution's peak
    const thisPeakVal = pdf(mu);
    // Use 120% of this distribution's peak, minimum 0.1
    const maxVal = Math.max(0.1, thisPeakVal * 1.2);

    for (let x = start; x <= end; x += step) {
      const xPos = (x / 50) * width;
      const yPos = height - (pdf(x) / maxVal) * (height * 0.8);
      points.push(`${xPos.toFixed(1)},${yPos.toFixed(1)}`);
    }
    return `M 0,${height} L ${points[0]} L ${points.join(' L ')} L ${width},${height} Z`;
  };

  // Calculate auto-scaling range
  const allMuValues = [
    player.mu,
    ...(comparison ? [comparison.mu] : []),
    ...allPlayers.map(p => p.mu),
  ];
  const minMu = Math.min(...allMuValues);
  const maxMu = Math.max(...allMuValues);
  const padding = (maxMu - minMu) * 0.2; // Add 20% padding
  const minRating = Math.max(0, minMu - padding);
  const maxRating = Math.min(50, maxMu + padding);

  const handleMouseMove = event => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const rating = (x / rect.width) * 50;
    const confidence =
      rating >= player.mu - 2 * player.sigma &&
      rating <= player.mu + 2 * player.sigma
        ? 'High'
        : 'Low';

    setHoveredPoint({
      x,
      y: event.clientY - rect.top,
      rating: rating.toFixed(1),
      confidence,
      mu: player.mu.toFixed(2),
      sigma: player.sigma.toFixed(2),
    });
  };

  return (
    <div className="h-64 w-full relative flex items-end pb-0 z-10 group">
      <svg
        className="w-full h-full cursor-crosshair"
        viewBox="0 0 400 200"
        preserveAspectRatio="none"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredPoint(null)}
      >
        <defs>
          <linearGradient id="curveGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#18181b" stopOpacity="0.1" />
            <stop offset="90%" stopColor="#18181b" stopOpacity="0.0" />
          </linearGradient>
          <linearGradient id="medianGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#a1a1aa" stopOpacity="0.05" />
            <stop offset="90%" stopColor="#a1a1aa" stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {/* All Players Median Baseline */}
        {allPlayers.length > 0 &&
          (() => {
            const medianMu =
              allPlayers.reduce((sum, p) => sum + p.mu, 0) / allPlayers.length;
            const medianSigma = Math.sqrt(
              allPlayers.reduce((sum, p) => sum + p.sigma ** 2, 0) /
                allPlayers.length
            );
            logger.debug('Median calculation:', {
              medianMu,
              medianSigma,
              allPlayersMu: allPlayers.map(p => p.mu),
              currentPlayerMu: player.mu,
              minRating,
              maxRating,
              chartRange: `${minRating.toFixed(1)} - ${maxRating.toFixed(1)}`,
              medianPosition:
                ((medianMu - minRating) / (maxRating - minRating)) * 100,
            });
            return (
              <path
                d={getPath(medianMu, medianSigma, 200, 400)}
                fill="url(#medianGradient)"
                stroke="#d4d4d8"
                strokeWidth="1.5"
                strokeDasharray="3 3"
              />
            );
          })()}

        {/* Comparison Player */}
        {comparison && (
          <path
            d={getPath(comparison.mu, comparison.sigma, 200, 400)}
            fill="none"
            stroke="#a1a1aa"
            strokeWidth="2"
            strokeDasharray="6 4"
          />
        )}

        {/* Active Player */}
        <path
          d={getPath(player.mu, player.sigma, 200, 400)}
          fill="url(#curveGradient)"
          stroke="#18181b"
          strokeWidth="2.5"
        />

        {/* Hover Indicator */}
        {hoveredPoint && (
          <>
            <line
              x1={(hoveredPoint.rating / 50) * 400}
              y1="0"
              x2={(hoveredPoint.rating / 50) * 400}
              y2="200"
              stroke="#18181b"
              strokeWidth="1"
              strokeDasharray="2 2"
              opacity="0.7"
            />
            <circle
              cx={(hoveredPoint.rating / 50) * 400}
              cy="20"
              r="3"
              fill="#18181b"
            />
          </>
        )}
      </svg>

      {/* Tooltip */}
      {hoveredPoint && (
        <div
          className="absolute bg-zinc-900 text-white text-[10px] p-2 rounded shadow-lg pointer-events-none z-50"
          style={{
            left: `${hoveredPoint.x}px`,
            top: `${Math.max(10, hoveredPoint.y - 60)}px`,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="flex flex-col items-center gap-1">
            <span className="font-bold">Rating: {hoveredPoint.rating}</span>
            <span className="text-zinc-400">
              Confidence: {hoveredPoint.confidence}
            </span>
            <span className="text-zinc-400">
              μ={hoveredPoint.mu}, σ={hoveredPoint.sigma}
            </span>
          </div>
          <div className="w-2 h-2 bg-zinc-900 absolute -bottom-1 left-1/2 -translate-x-1/2 rotate-45"></div>
        </div>
      )}

      <div className="absolute bottom-0 w-full flex justify-between text-[9px] text-zinc-400 font-bold uppercase tracking-widest px-1">
        <span>Novice (0)</span>
        <span>Average (25)</span>
        <span>Pro (50)</span>
      </div>
    </div>
  );
};

export default SkillDistributionChart;
